import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, ExternalLink, Copy, MapPin, Package, Pencil, DollarSign, Boxes, Check, X, CalendarDays } from 'lucide-react';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';

export default function EventsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const navigate = useNavigate();

  const fetchProjects = () => {
    setLoading(true);
    api.get('/event-layout/projects')
      .then(res => setProjects(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) return alert('제목을 입력해주세요.');
    setCreating(true);
    try {
      const res = await api.post('/event-layout/projects', form);
      navigate(`/events/${res.data.id}/edit`);
    } catch {
      alert('생성 중 오류가 발생했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`"${title}" 행사를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/event-layout/projects/${id}`);
      fetchProjects();
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const startEditing = (p) => {
    setEditingId(p.id);
    setEditForm({ title: p.title, description: p.description || '' });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({ title: '', description: '' });
  };

  const handleUpdate = async (id) => {
    if (!editForm.title.trim()) return alert('제목을 입력해주세요.');
    try {
      await api.put(`/event-layout/projects/${id}`, editForm);
      setProjects(prev => prev.map(p => p.id === id ? { ...p, ...editForm } : p));
      setEditingId(null);
    } catch {
      alert('수정 중 오류가 발생했습니다.');
    }
  };

  const handleDuplicate = async (id, title) => {
    if (!window.confirm(`"${title}" 행사를 복제하시겠습니까?`)) return;
    try {
      await api.post(`/event-layout/projects/${id}/duplicate`);
      fetchProjects();
    } catch {
      alert('복제 중 오류가 발생했습니다.');
    }
  };

  const copyLink = (shareId, editable) => {
    const base = window.location.origin;
    const url = editable
      ? `${base}/events/share/${shareId}?edit=1`
      : `${base}/events/view/${shareId}`;
    const msg = editable ? '편집 가능 링크가 복사되었습니다.' : '보기용 링크가 복사되었습니다.';
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => alert(msg));
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(msg);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <PageHeader icon={CalendarDays} title="행사관리" subtitle="행사별 배치도, 예산, 품목을 통합 관리하세요">
        <Link to="/events/assets" className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors">
          <Package size={14} /> 에셋 관리
        </Link>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-1.5 bg-brand hover:bg-brand/80 text-white text-sm rounded-lg transition-colors">
          <Plus size={14} /> 새 행사
        </button>
      </PageHeader>

      {/* 생성 모달 */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-medium text-white mb-4">새 행사</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">제목</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-brand"
                  placeholder="예: 2026 봄꽃축제" autoFocus />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">설명 (선택)</label>
                <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-brand"
                  placeholder="메모" />
              </div>
              <button type="submit" disabled={creating}
                className="w-full py-2.5 bg-brand hover:bg-brand/80 disabled:opacity-50 text-white text-sm rounded-lg transition-colors">
                {creating ? '생성 중...' : '생성 후 편집하기'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 프로젝트 목록 */}
      {loading ? (
        <div className="text-center text-gray-500 py-20">로딩 중...</div>
      ) : projects.length === 0 ? (
        <div className="text-center text-gray-500 py-20">
          <MapPin size={48} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg mb-2">등록된 행사가 없습니다</p>
          <p className="text-sm">새 행사를 추가하여 배치도, 예산, 품목을 관리하세요</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p => (
            <div key={p.id} className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 hover:border-brand/40 transition-colors group">
              <div className="flex items-start justify-between mb-3">
                {editingId === p.id ? (
                  <div className="flex-1 min-w-0 space-y-2">
                    <input type="text" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') handleUpdate(p.id); if (e.key === 'Escape') cancelEditing(); }}
                      className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm outline-none focus:border-brand"
                      autoFocus />
                    <input type="text" value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') handleUpdate(p.id); if (e.key === 'Escape') cancelEditing(); }}
                      className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-gray-400 text-xs outline-none focus:border-brand"
                      placeholder="설명" />
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleUpdate(p.id)} className="text-emerald-400 hover:text-emerald-300 transition-colors">
                        <Check size={15} />
                      </button>
                      <button onClick={cancelEditing} className="text-gray-500 hover:text-gray-300 transition-colors">
                        <X size={15} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{p.title}</h3>
                      {p.description && <p className="text-gray-500 text-sm mt-1 truncate">{p.description}</p>}
                    </div>
                    <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100">
                      <button onClick={() => startEditing(p)} title="수정"
                        className="text-gray-600 hover:text-brand transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDuplicate(p.id, p.title)} title="복제"
                        className="text-gray-600 hover:text-brand transition-colors">
                        <Copy size={15} />
                      </button>
                      <button onClick={() => handleDelete(p.id, p.title)} title="삭제"
                        className="text-gray-600 hover:text-red-400 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </>
                )}
              </div>

              <p className="text-gray-600 text-xs mb-4">{new Date(p.createdAt).toLocaleDateString('ko-KR')}</p>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Link to={`/events/${p.id}/edit`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-brand/20 hover:bg-brand/30 text-brand text-sm rounded-lg transition-colors">
                    <ExternalLink size={14} /> 배치도편집
                  </Link>
                  <button onClick={() => copyLink(p.shareId, false)} title="보기용 링크 복사"
                    className="p-2 bg-gray-800/50 hover:bg-gray-700 text-gray-500 hover:text-white rounded-lg transition-colors">
                    <Copy size={14} />
                  </button>
                  <button onClick={() => copyLink(p.shareId, true)} title="편집 가능 링크 복사"
                    className="p-2 bg-gray-800/50 hover:bg-gray-700 text-brand/50 hover:text-brand rounded-lg transition-colors">
                    <Pencil size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/events/${p.id}/budget`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 text-sm rounded-lg transition-colors">
                    <DollarSign size={14} /> 예산관리
                  </Link>
                  <Link to={`/events/${p.id}/items`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-sky-500/15 hover:bg-sky-500/25 text-sky-400 text-sm rounded-lg transition-colors">
                    <Boxes size={14} /> 렌탈관리
                  </Link>
                  <Link to={`/events/${p.id}/inventory`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 text-sm rounded-lg transition-colors">
                    <Package size={14} /> 물품관리
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
